import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CheckCircle,
  PackageSearch,
  ShoppingCart,
  Truck,
  Users
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const CENTRAL_URL = 'https://centrale.objectifscopoutremer.com';

const services = [
  {
    icon: ShoppingCart,
    title: 'Acheter mieux',
    description: 'Regroupez vos besoins, accédez à des conditions négociées et simplifiez vos approvisionnements.',
    bullets: ['Achats mutualisés', 'Demandes groupées', 'Suivi des opportunités'],
    href: CENTRAL_URL,
    cta: 'Découvrir Communityplace'
  },
  {
    icon: PackageSearch,
    title: 'Vendre davantage',
    description: 'Présentez votre offre aux membres de l’écosystème et développez de nouveaux débouchés.',
    bullets: ['Référencement fournisseur', 'Visibilité professionnelle', 'Mise en relation qualifiée'],
    href: CENTRAL_URL,
    cta: 'Découvrir la centrale professionnelle'
  },
  {
    icon: Users,
    title: 'Partager les compétences',
    description: 'Mobilisez les ressources adaptées à votre activité avec O’SCOP GE et les solutions de formation.',
    bullets: ['Emploi partagé', 'Recrutement', 'Montée en compétences'],
    to: '/oscop-ge',
    cta: 'Découvrir O’SCOP GE'
  },
  {
    icon: Truck,
    title: 'Organiser la logistique',
    description: 'Préparez l’acheminement de vos marchandises et identifiez les solutions adaptées au territoire.',
    bullets: ['Transport', 'Coordination logistique', 'Relais de proximité'],
    to: '/logistique-mobilite',
    cta: 'Découvrir LOGI’SCOP'
  }
];

const steps = [
  {
    number: '01',
    title: 'Choisissez votre service',
    description: 'Repérez la solution adaptée à vos achats, vos ventes, vos équipes ou votre logistique.'
  },
  {
    number: '02',
    title: 'Présentez votre besoin',
    description: 'Contactez l’équipe ou suivez le parcours proposé sur la page du service concerné.'
  },
  {
    number: '03',
    title: 'Précisez les modalités',
    description: 'Échangez sur le périmètre, les conditions d’accès et le calendrier avant de vous engager.'
  }
];

const ServicesEntreprisesPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navigation />

      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950" aria-hidden="true" />
          <div className="absolute -right-24 top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />

          <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-24 sm:px-6 lg:px-8 lg:pb-24 lg:pt-32">
            <BackButton to="/" label="Retour à l’accueil" />
            <div className="mt-10 grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-400/10 px-4 py-2 text-sm font-semibold text-blue-200">
                  <Building2 className="h-4 w-4" aria-hidden="true" />
                  SERVICES AUX ENTREPRISES
                </div>
                <h1 className="mt-6 text-4xl font-black tracking-tight sm:text-6xl">
                  Achetez, vendez, recrutez et transportez
                  <span className="block text-blue-300">avec un seul écosystème.</span>
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-relaxed text-slate-300">
                  O’SCOP aide les TPE, PME, associations et organisations ultramarines à mutualiser leurs moyens,
                  trouver des partenaires et passer plus vite de l’intention à l’action.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Link to="/register">
                    <Button size="lg" className="w-full bg-blue-600 px-8 py-6 text-base font-semibold hover:bg-blue-500 sm:w-auto">
                      Créer mon espace
                      <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                    </Button>
                  </Link>
                  <a href={CENTRAL_URL} target="_blank" rel="noreferrer">
                    <Button size="lg" variant="outline" className="w-full border-white/20 bg-white/5 px-8 py-6 text-base text-white hover:bg-white/10 sm:w-auto">
                      Accéder à la centrale pro
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full border-white/20 bg-white/5 px-8 py-6 text-base text-white hover:bg-white/10 sm:w-auto">
                      Parler à un conseiller
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="rounded-3xl border border-blue-400/20 bg-white/5 p-7 backdrop-blur sm:p-8">
                <BriefcaseBusiness className="h-10 w-10 text-blue-300" aria-hidden="true" />
                <h2 className="mt-5 text-2xl font-bold">Pour quel besoin ?</h2>
                <div className="mt-6 space-y-4">
                  {[
                    'Réduire ou sécuriser vos coûts d’achat',
                    'Trouver de nouveaux clients professionnels',
                    'Renforcer votre équipe ou ses compétences',
                    'Acheminer vos produits dans les territoires'
                  ].map((label) => (
                    <div key={label} className="flex items-start gap-3 text-slate-200">
                      <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-emerald-400" aria-hidden="true" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-widest text-blue-300">4 leviers opérationnels</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-5xl">Des services reliés à votre activité</h2>
              <p className="mt-4 text-lg text-slate-400">
                Commencez par votre priorité : l’écosystème vous permet ensuite d’activer les services complémentaires.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {services.map((service) => (
                <Card key={service.title} className="border-slate-800 bg-slate-900/70">
                  <CardContent className="p-7">
                    <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-blue-500/15 p-3">
                      <service.icon className="h-7 w-7 text-blue-300" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-2xl font-bold text-white">{service.title}</h3>
                    <p className="mt-3 leading-relaxed text-slate-400">{service.description}</p>
                    <ul className="mt-5 space-y-2">
                      {service.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckCircle className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    {service.href ? (
                      <a href={service.href} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200">
                        {service.cta}<ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </a>
                    ) : (
                      <Link to={service.to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200">
                        {service.cta}<ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-slate-900 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-emerald-300">Une démarche simple</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-5xl">Passez à l’action en trois étapes</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {steps.map((step) => (
                <div key={step.number} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-7">
                  <span className="text-sm font-black tracking-widest text-blue-300">{step.number}</span>
                  <h3 className="mt-4 text-xl font-bold">{step.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate-400">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-24">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-indigo-400/20 bg-gradient-to-r from-blue-950 to-indigo-950 p-8 text-center sm:p-12">
              <h2 className="text-3xl font-bold sm:text-4xl">Quel est votre prochain objectif ?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
                Créez votre espace pour démarrer ou décrivez votre besoin afin d’être orienté vers la solution adaptée.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <Link to="/register">
                  <Button size="lg" className="w-full bg-blue-600 px-9 py-6 text-base font-semibold hover:bg-blue-500 sm:w-auto">
                    Créer mon espace
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full border-white/20 bg-white/5 px-9 py-6 text-base text-white hover:bg-white/10 sm:w-auto">
                    Décrire mon besoin
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesEntreprisesPage;
